import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import Shell from "../components/Shell";
import { langMatches, loadPrefs } from "../prefs";
import { slugifySeries } from "../series";
import { saveSeriesResume } from "../seriesResume";

function installDetachedBufferGuard() {
  if (typeof window === "undefined" || window.__casasmAbGuard) return;
  window.__casasmAbGuard = true;
  const isDetachedAb = (msg) =>
    typeof msg === "string" &&
    (msg.includes("detached ArrayBuffer") ||
      msg.includes("Cannot perform Construct on a detached ArrayBuffer"));

  window.addEventListener(
    "error",
    (event) => {
      if (isDetachedAb(event.message)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );
  window.addEventListener(
    "unhandledrejection",
    (event) => {
      const reason = event.reason;
      const msg =
        typeof reason === "string"
          ? reason
          : reason?.message || String(reason || "");
      if (isDetachedAb(msg)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );
}

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m} min`;
}

function typeLabel(kind) {
  if (kind === "series") return "Serie";
  if (kind === "anime_series") return "Anime (serie)";
  if (kind === "anime") return "Película · Anime";
  return "Película";
}

function langName(item) {
  if (!item) return "";
  if (typeof item === "string") return item;
  return item.name || item.code || "";
}

function normalizeActors(actors) {
  if (!Array.isArray(actors)) return [];
  return actors.map((a) =>
    typeof a === "string"
      ? { name: a, character: null, profile_url: null, tmdb_id: null }
      : {
          name: a.name,
          character: a.character || null,
          profile_url: a.profile_url || null,
          tmdb_id: a.tmdb_id || null,
        }
  );
}

const CS_SVG_PLAY =
  '<svg class="vjs-casasm-svg-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>';
const CS_SVG_PAUSE =
  '<svg class="vjs-casasm-svg-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>';
const CS_SVG_REWIND =
  '<svg class="vjs-casasm-svg-icon vjs-casasm-svg-seek" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/></svg>';
const CS_SVG_FORWARD =
  '<svg class="vjs-casasm-svg-icon vjs-casasm-svg-seek" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/></svg>';

function installCasasmPlayerGestures(player, { isCancelled }) {
  const root = player.el();
  if (!root) return () => {};

  const SEEK_STEP = 10;
  let centerHideTimer = null;

  const layer = document.createElement("div");
  layer.className = "vjs-casasm-gesture-layer";
  layer.innerHTML = `
    <button type="button" class="vjs-casasm-edge-btn vjs-casasm-tap-left" aria-label="Retroceder 10 segundos">
      <span class="vjs-casasm-edge-btn-inner">
        ${CS_SVG_REWIND}
        <span class="vjs-casasm-edge-label">10</span>
      </span>
    </button>
    <button type="button" class="vjs-casasm-edge-btn vjs-casasm-tap-right" aria-label="Adelantar 10 segundos">
      <span class="vjs-casasm-edge-btn-inner">
        ${CS_SVG_FORWARD}
        <span class="vjs-casasm-edge-label">10</span>
      </span>
    </button>
    <button type="button" class="vjs-casasm-tap-center" aria-label="Mostrar controles de reproducción"></button>
    <button type="button" class="vjs-casasm-center-play" aria-label="Reproducir">
      <span class="vjs-casasm-center-play-icon">${CS_SVG_PLAY}</span>
      <span class="vjs-casasm-center-pause-icon" hidden>${CS_SVG_PAUSE}</span>
    </button>
  `;
  root.appendChild(layer);

  const leftZone = layer.querySelector(".vjs-casasm-tap-left");
  const centerZone = layer.querySelector(".vjs-casasm-tap-center");
  const rightZone = layer.querySelector(".vjs-casasm-tap-right");
  const leftInner = layer.querySelector(".vjs-casasm-tap-left .vjs-casasm-edge-btn-inner");
  const rightInner = layer.querySelector(".vjs-casasm-tap-right .vjs-casasm-edge-btn-inner");
  const playBtn = layer.querySelector(".vjs-casasm-center-play");
  const playIcon = layer.querySelector(".vjs-casasm-center-play-icon");
  const pauseIcon = layer.querySelector(".vjs-casasm-center-pause-icon");

  function syncPlayIcon() {
    if (player.isDisposed()) return;
    const paused = player.paused();
    if (playIcon) playIcon.hidden = !paused;
    if (pauseIcon) pauseIcon.hidden = paused;
    playBtn?.setAttribute("aria-label", paused ? "Reproducir" : "Pausar");
    playBtn?.classList.toggle("is-paused", paused);
    // No llamar showCenterPlay aquí: reentraría en syncPlayIcon y
    // provoca Maximum call stack size exceeded (paused ↔ showCenterPlay).
    if (!paused) {
      playBtn?.classList.remove("is-visible");
      layer.classList.remove("is-center-open");
    }
  }

  function flashEdge(el) {
    if (!el) return;
    el.classList.remove("is-flash");
    void el.offsetWidth;
    el.classList.add("is-flash");
    window.setTimeout(() => el.classList.remove("is-flash"), 700);
  }

  function hideEdgeVisuals() {
    leftInner?.classList.remove("is-flash");
    rightInner?.classList.remove("is-flash");
  }

  function seekBy(delta) {
    if (isCancelled() || player.isDisposed()) return;
    const dur = player.duration();
    const next = Math.max(0, (player.currentTime() || 0) + delta);
    player.currentTime(Number.isFinite(dur) && dur > 0 ? Math.min(next, dur) : next);
    player.userActive(true);
    syncPlayIcon();
  }

  function togglePlay() {
    if (isCancelled() || player.isDisposed()) return;
    if (player.paused()) {
      const p = player.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } else {
      player.pause();
    }
    player.userActive(true);
    syncPlayIcon();
  }

  function hideCenterPlay() {
    if (!playBtn || player.isDisposed() || player.paused()) return;
    playBtn.classList.remove("is-visible");
    layer.classList.remove("is-center-open");
  }

  function showCenterPlay(autoHide = true) {
    if (!playBtn) return;
    syncPlayIcon();
    playBtn.classList.add("is-visible");
    layer.classList.add("is-center-open");
    player.userActive(true);
    if (centerHideTimer) window.clearTimeout(centerHideTimer);
    if (autoHide && !player.paused()) {
      centerHideTimer = window.setTimeout(hideCenterPlay, 3200);
    }
  }

  function onSeekBack() {
    seekBy(-SEEK_STEP);
    flashEdge(leftInner);
  }

  function onSeekForward() {
    seekBy(SEEK_STEP);
    flashEdge(rightInner);
  }

  const onPlay = () => {
    hideEdgeVisuals();
    syncPlayIcon();
  };
  const onPause = () => {
    syncPlayIcon();
    showCenterPlay(false);
  };

  leftZone?.addEventListener("click", (e) => {
    e.stopPropagation();
    onSeekBack();
  });
  rightZone?.addEventListener("click", (e) => {
    e.stopPropagation();
    onSeekForward();
  });
  centerZone?.addEventListener("click", (e) => {
    e.stopPropagation();
    if (player.paused()) {
      togglePlay();
      return;
    }
    showCenterPlay(true);
  });
  playBtn?.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    togglePlay();
  });

  player.on("play", onPlay);
  player.on("pause", onPause);
  syncPlayIcon();
  if (!player.isDisposed() && player.paused()) {
    showCenterPlay(false);
  }

  let touchStart = null;
  const onTouchStart = (e) => {
    const t = e.changedTouches?.[0];
    if (!t) return;
    touchStart = { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onTouchEnd = (e) => {
    if (isCancelled() || player.isDisposed()) return;
    const t = e.changedTouches?.[0];
    if (!t || !touchStart) return;
    const dx = Math.abs(t.clientX - touchStart.x);
    const dy = Math.abs(t.clientY - touchStart.y);
    const dt = Date.now() - touchStart.t;
    touchStart = null;
    if (dx > 18 || dy > 18 || dt > 500) return;
    e.preventDefault();
    const target = document.elementFromPoint(t.clientX, t.clientY);
    if (leftZone?.contains(target)) {
      onSeekBack();
      return;
    }
    if (rightZone?.contains(target)) {
      onSeekForward();
      return;
    }
    if (playBtn?.contains(target)) return;
    const rect = root.getBoundingClientRect();
    const ratio = (t.clientX - rect.left) / Math.max(rect.width, 1);
    if (ratio < 0.34) {
      onSeekBack();
      return;
    }
    if (ratio > 0.66) {
      onSeekForward();
      return;
    }
    if (player.paused()) {
      togglePlay();
      return;
    }
    showCenterPlay(true);
  };

  root.addEventListener("touchstart", onTouchStart, { passive: true });
  root.addEventListener("touchend", onTouchEnd, { passive: false });

  return () => {
    if (centerHideTimer) window.clearTimeout(centerHideTimer);
    player.off("play", onPlay);
    player.off("pause", onPause);
    root.removeEventListener("touchstart", onTouchStart);
    root.removeEventListener("touchend", onTouchEnd);
    layer.remove();
  };
}

export default function WatchPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, profile, isKidsProfile } = useAuth();
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const nextEpisodeRef = useRef(null);
  const autoplayNextRef = useRef(Boolean(location.state?.autoplayNext));
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [audioOptions, setAudioOptions] = useState([]);
  const [subtitleOptions, setSubtitleOptions] = useState([
    { id: "off", label: "Sin subtítulos" },
  ]);
  const [audioId, setAudioId] = useState("");
  const [subtitleId, setSubtitleId] = useState("off");
  const [audioHint, setAudioHint] = useState("");
  const [actorModal, setActorModal] = useState(null);
  const [actorBusy, setActorBusy] = useState(false);
  const [actorError, setActorError] = useState("");
  const [playerUiRoot, setPlayerUiRoot] = useState(null);
  const [myRating, setMyRating] = useState(0);
  const [ratingAvg, setRatingAvg] = useState(null);
  const prefsAppliedRef = useRef(false);
  const [ratingCount, setRatingCount] = useState(0);
  const [ratingBusy, setRatingBusy] = useState(false);
  const [kidsModalOpen, setKidsModalOpen] = useState(false);
  const [kidsModalClosing, setKidsModalClosing] = useState(false);
  const kidsCloseTimerRef = useRef(null);
  const bannerParallaxRef = useRef(null);
  const stageParallaxRef = useRef(null);
  const castParallaxRef = useRef(null);

  useEffect(() => {
    installDetachedBufferGuard();
  }, []);

  useEffect(() => {
    if (!actorModal) return undefined;
    function onKey(e) {
      if (e.key === "Escape") setActorModal(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [actorModal]);

  useEffect(() => {
    if (!info || isKidsProfile) return undefined;

    const sections = [
      bannerParallaxRef.current,
      stageParallaxRef.current,
      castParallaxRef.current,
    ].filter(Boolean);
    if (!sections.length) return undefined;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let ticking = false;

    function update() {
      ticking = false;
      // En fullscreen el GPU del vídeo es crítico: no muevas capas debajo.
      if (document.documentElement.classList.contains("cs-player-fs")) {
        sections.forEach((el) => {
          el.style.setProperty("--parallax-y", "0px");
          el.style.setProperty("--parallax-y-inv", "0px");
        });
        return;
      }
      const vh = window.innerHeight || 1;
      sections.forEach((el) => {
        const speed = Number(el.dataset.parallaxSpeed || 0.14);
        const rect = el.getBoundingClientRect();
        const mid = rect.top + rect.height * 0.45;
        const progress = (mid - vh * 0.5) / vh;
        const offset = reduceMotion ? 0 : progress * -140 * speed;
        el.style.setProperty("--parallax-y", `${offset.toFixed(2)}px`);
        el.style.setProperty("--parallax-y-inv", `${(-offset * 0.4).toFixed(2)}px`);
        const visible = rect.top < vh * 0.88 && rect.bottom > vh * 0.1;
        el.classList.toggle("is-inview", visible);
      });
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-inview");
        });
        onScroll();
      },
      { threshold: [0.12, 0.28, 0.5], rootMargin: "0px 0px -8% 0px" }
    );
    sections.forEach((el) => io.observe(el));

    update();
    const boot = window.setTimeout(update, 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.clearTimeout(boot);
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [info]);

  useEffect(() => {
    prefsAppliedRef.current = false;
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setInfo(null);
    setError("");
    setAudioOptions([]);
    setAudioId("");
    setSubtitleId("off");
    api(`/api/videos/${id}/play`, { token })
      .then((data) => {
        if (cancelled) return;
        setInfo(data);
        setMyRating(Number(data.video?.my_rating) || 0);
        setRatingAvg(data.video?.rating_avg ?? null);
        setRatingCount(Number(data.video?.rating_count) || 0);
        const tracks = (data.audio_tracks || []).filter((t) => t?.label || t?.language);
        if (tracks.length) {
          setAudioOptions(
            tracks.map((t) => {
              const lang = (t.language || t.id || "").toLowerCase();
              return {
                id: lang || t.id,
                label: t.label || lang,
                language: lang,
                live: true,
              };
            })
          );
          const def = tracks.find((t) => t.default) || tracks[0];
          setAudioId((def.language || def.id || "").toLowerCase());
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [id, token]);

  useEffect(() => {
    nextEpisodeRef.current = info?.next_episode || null;
  }, [info]);

  useEffect(() => {
    autoplayNextRef.current = Boolean(location.state?.autoplayNext);
  }, [location.state, id]);

  useEffect(() => {
    if (!info?.hls_url || !containerRef.current) return undefined;

    let cancelled = false;
    let seekedToProgress = false;
    let disposeGestures = () => {};
    const trackTimers = [];
    setError("");

    const videoEl = document.createElement("video-js");
    videoEl.classList.add("vjs-big-play-centered", "vjs-theme-casasm");
    containerRef.current.innerHTML = "";
    containerRef.current.appendChild(videoEl);

    // Safari ya reproduce HLS nativo; forzar VHS ahí provoca MEDIA_ERR_SRC_NOT_SUPPORTED.
    const isSafari =
      Boolean(videojs.browser?.IS_ANY_SAFARI) || Boolean(videojs.browser?.IS_IOS);
    const player = videojs(videoEl, {
      controls: true,
      bigPlayButton: false,
      fluid: false,
      fill: true,
      autoplay: Boolean(autoplayNextRef.current),
      preload: "auto",
      inactivityTimeout: 3200,
      playbackRates: [0.75, 1, 1.25, 1.5, 2],
      preferFullWindow: false,
      fullscreen: {
        options: { navigationUI: "hide" },
      },
      // Click / tap en el vídeo = play/pause (también en táctil).
      userActions: {
        click: false,
        doubleClick: true,
        hotkeys: true,
      },
      controlBar: {
        children: [
          "playToggle",
          "currentTimeDisplay",
          "timeDivider",
          "durationDisplay",
          "progressControl",
          "playbackRateMenuButton",
          "fullscreenToggle",
        ],
      },
      html5: {
        vhs: {
          overrideNative: !isSafari,
          limitRenditionByPlayerDimensions: false,
          enableLowInitialPlaylist: true,
          smoothQualityChange: false,
          allowSeeksWithinUnsafeLiveWindow: true,
          maxPlaylistRetries: 6,
        },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
        nativeTextTracks: false,
      },
    });
    playerRef.current = player;

    function markFullscreenUi(fs) {
      document.documentElement.classList.toggle("cs-player-fs", fs);
      document.body.classList.toggle("cs-player-fs", fs);
    }

    function syncFullscreen() {
      if (cancelled || player.isDisposed()) return;
      const fs = Boolean(
        player.isFullscreen?.() ||
          document.fullscreenElement === player.el() ||
          document.webkitFullscreenElement === player.el()
      );
      markFullscreenUi(fs);
      // Sin setState de React: evita re-renders que traban el decode en FS.
    }

    player.ready(() => {
      if (cancelled || player.isDisposed()) return;
      const bar = player.el()?.querySelector?.(".vjs-control-bar");
      let slot = player.el()?.querySelector?.(".vjs-casasm-tracks-slot");
      if (bar && !slot) {
        slot = document.createElement("div");
        slot.className = "vjs-casasm-tracks-slot";
        const rate = bar.querySelector(".vjs-playback-rate");
        const fs = bar.querySelector(".vjs-fullscreen-control");
        const anchor = fs || rate;
        if (anchor) {
          bar.insertBefore(slot, anchor);
        } else {
          bar.appendChild(slot);
        }
      }
      setPlayerUiRoot(slot || player.el());

      disposeGestures = installCasasmPlayerGestures(player, {
        isCancelled: () => cancelled,
      });

      syncFullscreen();
    });
    player.on("fullscreenchange", syncFullscreen);
    document.addEventListener("fullscreenchange", syncFullscreen);
    document.addEventListener("webkitfullscreenchange", syncFullscreen);

    function getVhsAudioTracks(player) {
      try {
        const tech = player.tech({ IWillNotUseThisInPlugins: true });
        const vhsTracks = tech?.vhs?.mediaTypes_?.AUDIO?.tracks;
        if (vhsTracks && typeof vhsTracks === "object") {
          return Object.keys(vhsTracks).map((key) => {
            const t = vhsTracks[key];
            return {
              key,
              track: t,
              label: t.label || key,
              language: (t.language || "").toLowerCase(),
              enabled: Boolean(t.enabled),
            };
          });
        }
      } catch (_) {
        /* ignore */
      }
      return [];
    }

    function listLiveAudioTracks(player) {
      const vhs = getVhsAudioTracks(player);
      if (vhs.length) {
        return vhs.map((t, i) => ({
          id: t.language || t.key || String(i),
          label: t.label,
          language: t.language,
          vhsKey: t.key,
          live: true,
        }));
      }
      const tracks = player.audioTracks?.();
      const live = [];
      if (!tracks?.length) return live;
      for (let i = 0; i < tracks.length; i += 1) {
        const t = tracks[i];
        live.push({
          id: (t.language || String(i)).toLowerCase(),
          label: t.label || t.language || `Pista ${i + 1}`,
          language: (t.language || "").toLowerCase(),
          index: i,
          live: true,
        });
      }
      return live;
    }

    function refreshTracks() {
      if (cancelled || player.isDisposed()) return;
      const live = listLiveAudioTracks(player);
      const fromManifest = (info.audio_tracks || []).filter((t) => t?.label || t?.language);

      if (live.length >= 1) {
        // Preferir labels del manifest si VHS solo da nombres técnicos.
        const merged =
          fromManifest.length >= live.length
            ? fromManifest.map((t) => {
                const lang = (t.language || t.id || "").toLowerCase();
                const hit =
                  live.find((l) => l.language === lang) ||
                  live.find((l) => (l.label || "").toLowerCase() === (t.label || "").toLowerCase());
                return {
                  id: lang || hit?.id || t.id,
                  label: t.label || hit?.label || lang,
                  language: lang,
                  vhsKey: hit?.vhsKey,
                  index: hit?.index,
                  live: Boolean(hit) || live.length >= fromManifest.length,
                };
              })
            : live;
        setAudioOptions(merged);
        const enabled =
          getVhsAudioTracks(player).find((t) => t.enabled) ||
          live.find((_, i) => player.audioTracks?.()?.[i]?.enabled);
        const enabledLang =
          enabled?.language ||
          merged.find((m) => m.language && enabled?.label === m.label)?.language ||
          merged[0]?.id;
        setAudioId(enabledLang || merged[0]?.id || "");
        setAudioHint(
          merged.filter((m) => m.live).length >= 2
            ? ""
            : fromManifest.length > 1
              ? "Pistas detectadas en el archivo; el player aún sincroniza el HLS…"
              : ""
        );
      } else if (fromManifest.length) {
        setAudioOptions(
          fromManifest.map((t) => {
            const lang = (t.language || t.id || "").toLowerCase();
            return {
              id: lang || t.id,
              label: t.label || lang,
              language: lang,
              live: true,
            };
          })
        );
        const def = fromManifest.find((t) => t.default) || fromManifest[0];
        setAudioId((def.language || def.id || "").toLowerCase());
        setAudioHint("");
      } else {
        setAudioOptions([{ id: "default", label: "Audio principal", live: false }]);
        setAudioId("default");
        setAudioHint("");
      }

      const textTracks = player.textTracks?.();
      const nextSubs = [{ id: "off", label: "Sin subtítulos" }];
      if (textTracks) {
        for (let i = 0; i < textTracks.length; i += 1) {
          const track = textTracks[i];
          if (track.kind !== "subtitles" && track.kind !== "captions") continue;
          nextSubs.push({
            id: String(i),
            label: track.label || track.language || `Sub ${i + 1}`,
          });
        }
      }
      setSubtitleOptions(nextSubs);
    }

    function applyResume() {
      if (cancelled || player.isDisposed() || seekedToProgress) return;
      const pos = Number(info.position_seconds) || 0;
      if (pos <= 0) return;
      try {
        player.currentTime(pos);
        seekedToProgress = true;
      } catch (_) {
        /* ignore */
      }
    }

    function forceDefaultAudio() {
      if (cancelled || player.isDisposed()) return;
      const def =
        (info.audio_tracks || []).find((t) => t?.default) ||
        (info.audio_tracks || []).find((t) =>
          String(t?.language || t?.id || "").toLowerCase().startsWith("spa")
        ) ||
        (info.audio_tracks || [])[0];
      if (!def) return;
      const uiId = (def.language || def.id || "").toLowerCase();
      // Web/VHS: usar el LANGUAGE real del master HLS.
      // enable_language es un workaround solo para Tizen (etiquetas cruzadas);
      // forzarlo aquí selecciona la pista alterna (a veces solo-audio) y
      // provoca MEDIA_ERR_SRC_NOT_SUPPORTED en Chromium.
      const want = String(def.language || def.id || "").toLowerCase();
      if (!want) return;
      try {
        const tech = player.tech({ IWillNotUseThisInPlugins: true });
        const vhsAudio = tech?.vhs?.mediaTypes_?.AUDIO;
        const vhsTracks = vhsAudio?.tracks;
        if (!vhsTracks) return;
        const keys = Object.keys(vhsTracks);
        let target = null;
        for (const key of keys) {
          const t = vhsTracks[key];
          const lang = (t.language || "").toLowerCase();
          if (lang === want || key.toLowerCase() === want || lang.startsWith(want)) {
            target = key;
            break;
          }
        }
        if (target == null && want === "eng") {
          target = keys.find((k) => {
            const t = vhsTracks[k];
            const lang = (t.language || "").toLowerCase();
            return lang === "eng" || lang === "en" || (t.label || "").toLowerCase().includes("audio b");
          });
        }
        if (target == null) return;
        const already = vhsTracks[target]?.enabled;
        if (already) {
          setAudioId(uiId || want);
          return;
        }
        keys.forEach((key) => {
          vhsTracks[key].enabled = key === target;
        });
        if (typeof vhsAudio.onTrackChanged === "function") {
          vhsAudio.onTrackChanged();
        }
        setAudioId(uiId || want);
      } catch (_) {
        /* ignore */
      }
    }

    player.ready(() => {
      if (cancelled || player.isDisposed()) return;
      player.src({ src: info.hls_url, type: "application/x-mpegURL" });
      (info.subtitle_tracks || []).forEach((track) => {
        player.addRemoteTextTrack(
          {
            kind: "subtitles",
            srclang: track.language || track.id,
            label: track.label || track.language || track.id,
            src: track.src,
            default: false,
          },
          false
        );
      });
      refreshTracks();
      [400, 1000, 2000, 4000, 8000].forEach((ms) => {
        trackTimers.push(window.setTimeout(() => {
          refreshTracks();
          forceDefaultAudio();
        }, ms));
      });
    });
    player.on("loadedmetadata", () => {
      applyResume();
      refreshTracks();
      forceDefaultAudio();
      if (autoplayNextRef.current) {
        autoplayNextRef.current = false;
        try {
          player.play()?.catch?.(() => {});
        } catch (_) {
          /* ignore */
        }
      }
    });
    player.on("loadeddata", () => {
      refreshTracks();
      forceDefaultAudio();
    });
    const audioList = player.audioTracks?.();
    if (audioList?.addEventListener) {
      audioList.addEventListener("addtrack", refreshTracks);
      audioList.addEventListener("change", refreshTracks);
    }
    const textList = player.textTracks?.();
    if (textList?.addEventListener) {
      textList.addEventListener("addtrack", refreshTracks);
      textList.addEventListener("change", refreshTracks);
    }

    player.on("error", () => {
      if (cancelled || player.isDisposed()) return;
      const mediaError = player.error();
      const code = mediaError?.code;
      const msg = mediaError?.message || "No se pudo reproducir el HLS.";
      // CODE 4 al vaciar fuentes en cleanup/reset no es un fallo real de red/códec.
      if (code === 4 && !player.currentSrc()) return;
      setError(
        code
          ? `${msg} (código ${code}). URL: ${info.hls_url}`
          : `${msg} URL: ${info.hls_url}`
      );
    });

    const saveProgress = () => {
      if (cancelled || !player || player.isDisposed()) return;
      let position = 0;
      try {
        position = Math.floor(player.currentTime() || 0);
      } catch (_) {
        return;
      }
      if (position <= 0) return;
      api(`/api/videos/${id}/progress`, {
        method: "PUT",
        token,
        body: { position_seconds: position },
      }).catch(() => {});
    };
    const interval = setInterval(saveProgress, 20000);
    player.on("pause", saveProgress);

    player.on("ended", () => {
      if (cancelled || player.isDisposed()) return;
      // Marca el capítulo como visto al final.
      try {
        const dur = Math.floor(player.duration() || 0);
        if (dur > 0) {
          api(`/api/videos/${id}/progress`, {
            method: "PUT",
            token,
            body: { position_seconds: dur },
          }).catch(() => {});
        } else {
          saveProgress();
        }
      } catch (_) {
        saveProgress();
      }

      const next = nextEpisodeRef.current;
      // Solo sigue dentro de la misma temporada; si no hay siguiente, termina.
      if (next?.id && String(next.id) !== String(id)) {
        navigate(`/watch/${next.id}`, {
          state: {
            autoplayNext: true,
            background: location.state?.background,
          },
        });
      }
    });

    // Si en FS nativo el pipeline se queda en waiting, reanuda sin seek.
    let waitingSince = 0;
    let wantPlaying = false;
    player.on("play", () => {
      wantPlaying = true;
      waitingSince = 0;
    });
    player.on("playing", () => {
      wantPlaying = true;
      waitingSince = 0;
    });
    player.on("pause", () => {
      // pause manual del usuario (no stall)
      if (!player.seeking() && player.readyState() >= 3) wantPlaying = false;
    });
    player.on("waiting", () => {
      if (!waitingSince) waitingSince = Date.now();
    });
    player.on("canplay", () => {
      waitingSince = 0;
    });
    const stallWatch = window.setInterval(() => {
      if (cancelled || player.isDisposed()) return;
      if (!wantPlaying) return;
      if (!document.documentElement.classList.contains("cs-player-fs")) return;
      if (!waitingSince || Date.now() - waitingSince < 4000) return;
      try {
        if (player.paused()) player.play()?.catch?.(() => {});
      } catch (_) {
        /* ignore */
      }
      waitingSince = Date.now();
    }, 2000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.clearInterval(stallWatch);
      trackTimers.forEach((t) => clearTimeout(t));
      saveProgress();
      setPlayerUiRoot(null);
      disposeGestures();
      document.documentElement.classList.remove("cs-player-fs");
      document.body.classList.remove("cs-player-fs");
      playerRef.current = null;
      document.removeEventListener("fullscreenchange", syncFullscreen);
      document.removeEventListener("webkitfullscreenchange", syncFullscreen);
      if (!player || player.isDisposed()) return;
      try {
        player.off("error");
        player.dispose();
      } catch (_) {
        /* ignore */
      }
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, [info, id, token]);

  function onAudioChange(value) {
    setAudioId(value);
    const player = playerRef.current;
    if (!player || player.isDisposed()) return;

    const opt = audioOptions.find((o) => o.id === value);
    // Web: language/id del master. enable_language es solo para Tizen.
    const wantLang = String(opt?.language || value || "").toLowerCase();
    const wantLabel = (opt?.label || "").toLowerCase();

    let switched = false;

    // 1) VHS mediaTypes (fuente real del master HLS multi-audio)
    try {
      const tech = player.tech({ IWillNotUseThisInPlugins: true });
      const vhsAudio = tech?.vhs?.mediaTypes_?.AUDIO;
      const vhsTracks = vhsAudio?.tracks;
      if (vhsTracks) {
        const keys = Object.keys(vhsTracks);
        let targetKey = null;
        for (const key of keys) {
          const t = vhsTracks[key];
          const lang = (t.language || "").toLowerCase();
          const label = (t.label || key || "").toLowerCase();
          if (
            (wantLang && (lang === wantLang || key.toLowerCase() === wantLang || lang.startsWith(wantLang))) ||
            (wantLang === "eng" && (lang === "en" || label.includes("english") || label.includes("audio b"))) ||
            (wantLang === "spa" && (lang === "es" || label.includes("audio a")))
          ) {
            targetKey = key;
            break;
          }
        }
        if (targetKey != null) {
          keys.forEach((key) => {
            vhsTracks[key].enabled = key === targetKey;
          });
          // Dispara onTrackChanged de VHS
          if (typeof vhsAudio.onTrackChanged === "function") {
            vhsAudio.onTrackChanged();
          }
          switched = true;
        }
      }
    } catch (_) {
      /* ignore */
    }

    // 2) Fallback AudioTrackList del player
    if (!switched) {
      const tracks = player.audioTracks?.();
      if (tracks?.length) {
        let target = -1;
        for (let i = 0; i < tracks.length; i += 1) {
          const lang = (tracks[i].language || "").toLowerCase();
          const label = (tracks[i].label || "").toLowerCase();
          if (
            (wantLang && lang === wantLang) ||
            (wantLang && lang.startsWith(wantLang)) ||
            String(i) === String(value)
          ) {
            target = i;
            break;
          }
        }
        if (target < 0 && tracks.length >= 2) {
          if (wantLang === "eng") target = 1;
          else if (wantLang === "spa") target = 0;
        }
        if (target >= 0) {
          for (let i = 0; i < tracks.length; i += 1) tracks[i].enabled = false;
          tracks[target].enabled = true;
          switched = true;
        }
      }
    }

    if (!switched) {
      setAudioHint(
        "No se pudo cambiar el audio en el player. Recarga la página; si sigue igual, reprocesa el título en Admin."
      );
      return;
    }

    setAudioHint(`Audio activo: ${opt?.label || value}`);
    try {
      const t = player.currentTime();
      if (Number.isFinite(t) && t > 0.25) {
        player.currentTime(Math.max(0, t - 0.05));
      }
      const p = player.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch (_) {
      /* ignore */
    }
  }

  function onSubtitleChange(value) {
    setSubtitleId(value);
    const player = playerRef.current;
    if (!player || player.isDisposed()) return;
    const tracks = player.textTracks?.();
    if (!tracks) return;
    for (let i = 0; i < tracks.length; i += 1) {
      const track = tracks[i];
      if (track.kind !== "subtitles" && track.kind !== "captions") continue;
      track.mode =
        value !== "off" && String(i) === String(value) ? "showing" : "disabled";
    }
  }

  useEffect(() => {
    if (prefsAppliedRef.current) return;
    if (!info?.hls_url) return;
    if (!audioOptions.length) return;

    const prefs = loadPrefs();
    const waitingSubs =
      Boolean(prefs.subtitleLang) &&
      prefs.subtitleLang !== "off" &&
      subtitleOptions.length <= 1;
    if (waitingSubs) return;

    const timer = window.setTimeout(() => {
      if (prefsAppliedRef.current) return;
      const player = playerRef.current;
      if (!player || player.isDisposed()) return;

      prefsAppliedRef.current = true;

      if (prefs.audioLang) {
        const match = audioOptions.find((o) =>
          langMatches(`${o.language || ""} ${o.id || ""} ${o.label || ""}`, prefs.audioLang)
        );
        if (match) onAudioChange(match.id);
      }

      if (prefs.subtitleLang === "off") {
        onSubtitleChange("off");
      } else if (prefs.subtitleLang) {
        const match = subtitleOptions.find(
          (o) =>
            o.id !== "off" &&
            langMatches(`${o.label || ""} ${o.language || ""}`, prefs.subtitleLang)
        );
        if (match) onSubtitleChange(match.id);
      }
    }, 1400);

    return () => window.clearTimeout(timer);
  }, [info?.hls_url, audioOptions, subtitleOptions]);

  async function rateVideo(stars) {
    if (!token || ratingBusy) return;
    setRatingBusy(true);
    try {
      const data = await api(`/api/videos/${id}/rating`, {
        method: "PUT",
        token,
        body: { stars },
      });
      setMyRating(Number(data.my_rating) || stars);
      setRatingAvg(data.rating_avg ?? null);
      setRatingCount(Number(data.rating_count) || 0);
    } catch (err) {
      setError(err.message || "No se pudo guardar la puntuación.");
    } finally {
      setRatingBusy(false);
    }
  }

  async function openActor(actor) {
    setActorError("");
    setActorModal({
      actor,
      person: null,
    });
    if (!actor.tmdb_id) {
      setActorError("No hay ficha TMDB para este actor.");
      return;
    }
    setActorBusy(true);
    try {
      const person = await api(`/api/tmdb/person/${actor.tmdb_id}`, { token });
      setActorModal({ actor, person });
    } catch (err) {
      setActorError(err.message || "No se pudo cargar la ficha.");
    } finally {
      setActorBusy(false);
    }
  }

  const video = info?.video;
  const durationLabel = formatDuration(video?.duration_seconds);
  const actors = useMemo(() => normalizeActors(video?.actors), [video]);
  const languages = useMemo(
    () => (video?.languages || []).map(langName).filter(Boolean),
    [video]
  );
  const seriesSlug = useMemo(() => {
    if (!video) return null;
    if (video.content_type !== "series" && video.content_type !== "anime_series") {
      return null;
    }
    const title = (video.series_title || "").trim() || String(video.title || "").split(" · ")[0];
    let slug = slugifySeries(title);
    if (video.tmdb_id) slug = `${slug}-${video.tmdb_id}`;
    return slug;
  }, [video]);

  const seriesSeason =
    video?.season_number == null || Number.isNaN(Number(video?.season_number))
      ? 1
      : Number(video.season_number);

  useEffect(() => {
    if (!seriesSlug || !video) return;
    saveSeriesResume(profile?.id, seriesSlug, {
      season: seriesSeason,
      episodeId: video.id,
    });
  }, [seriesSlug, seriesSeason, video?.id, profile?.id]);

  const backTo = seriesSlug
    ? `/series/${seriesSlug}?season=${seriesSeason}`
    : video?.content_type === "anime_series"
      ? "/anime"
      : "/peliculas";

  const chips = useMemo(() => {
    if (!video) return [];
    return [video.year, video.category, durationLabel].filter(Boolean);
  }, [video, durationLabel]);

  function closeKidsPlayer() {
    if (kidsModalClosing) return;
    setKidsModalClosing(true);
    kidsCloseTimerRef.current = window.setTimeout(() => {
      const player = playerRef.current;
      if (player && !player.isDisposed?.()) {
        try {
          player.pause();
        } catch (_) {
          /* ignore */
        }
      }
      if (window.history.length > 1) navigate(-1);
      else navigate("/kids");
    }, 360);
  }

  useEffect(() => {
    if (!isKidsProfile) return undefined;
    document.body.classList.add("kids-watch-open");
    const openTimer = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setKidsModalOpen(true));
    });
    function onKey(e) {
      if (e.key === "Escape") closeKidsPlayer();
    }
    window.addEventListener("keydown", onKey);
    return () => {
      window.cancelAnimationFrame(openTimer);
      document.body.classList.remove("kids-watch-open");
      window.removeEventListener("keydown", onKey);
      if (kidsCloseTimerRef.current) {
        window.clearTimeout(kidsCloseTimerRef.current);
      }
    };
  }, [isKidsProfile, navigate]);

  const playerTrackUi =
    playerUiRoot &&
    createPortal(
      <div
        className="vjs-casasm-trackbar"
        aria-label="Audio y subtítulos"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <label className="watch-track-pill watch-track-pill--text" title="Pista de audio">
          <span className="watch-track-label">Audio</span>
          <select
            className="watch-track-select"
            value={audioId}
            onChange={(e) => onAudioChange(e.target.value)}
            aria-label="Audio"
          >
            {audioOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="watch-track-pill watch-track-pill--text" title="Subtítulos">
          <span className="watch-track-label">Subtítulos</span>
          <select
            className="watch-track-select"
            value={subtitleId}
            onChange={(e) => onSubtitleChange(e.target.value)}
            aria-label="Subtítulos"
          >
            {subtitleOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        {audioHint ? (
          <span className="watch-hint muted" title={audioHint}>
            {audioHint}
          </span>
        ) : null}
      </div>,
      playerUiRoot
    );

  if (isKidsProfile) {
    const modalArt =
      video?.backdrop_url || video?.cover_url || video?.series_backdrop_url || null;
    const modalClass = [
      "kids-watch-modal",
      kidsModalOpen ? "is-open" : "",
      kidsModalClosing ? "is-closing" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return createPortal(
      <div
        className={modalClass}
        role="dialog"
        aria-modal="true"
        aria-label={video?.title || "Reproductor"}
      >
        {modalArt ? (
          <div
            className="kids-watch-modal-bg-art"
            style={{ "--kids-watch-bg": `url(${modalArt})` }}
            aria-hidden="true"
          />
        ) : null}
        <div className="kids-watch-modal-scrim" aria-hidden="true" />
        <button
          type="button"
          className="kids-watch-modal-backdrop"
          aria-label="Cerrar reproductor"
          onClick={closeKidsPlayer}
        />
        <div className="kids-watch-modal-shell">
          <button
            type="button"
            className="kids-watch-modal-close"
            aria-label="Cerrar"
            onClick={closeKidsPlayer}
          >
            ×
          </button>
          <div className="kids-watch-modal-panel">
            {error ? <p className="error kids-watch-modal-message">{error}</p> : null}
            {!info && !error ? (
              <p className="kids-watch-modal-message">Preparando reproducción…</p>
            ) : null}
            {info && video ? (
              <div className="kids-watch-modal-player">
                <div
                  data-vjs-player
                  className="player-wrap watch-player"
                  ref={containerRef}
                />
                {playerTrackUi}
              </div>
            ) : null}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const Layout = Shell;

  return (
    <Layout wide>
      <div className={`watch-page ${info && video ? "is-ready" : ""}`}>
        {error ? <p className="error catalog-status watch-error">{error}</p> : null}

        {info && video ? (
          <>
            <section
              className="watch-banner watch-parallax"
              ref={bannerParallaxRef}
              data-parallax-speed="0.22"
              style={
                video.backdrop_url || video.cover_url
                  ? {
                      "--watch-bg": `url(${video.backdrop_url || video.cover_url})`,
                    }
                  : undefined
              }
            >
              <div className="watch-banner-bg watch-parallax-layer" aria-hidden="true" />
              <div className="watch-banner-glow" aria-hidden="true" />
              <div className="watch-banner-fade" aria-hidden="true" />

              <div className="watch-banner-inner">
                <Link to={backTo} className="back-link watch-back">
                  {seriesSlug
                    ? `← Volver a temporada ${seriesSeason}`
                    : "← Volver al catálogo"}
                </Link>

                <div className="watch-banner-grid">
                  <div className="watch-banner-poster">
                    {video.cover_url ? (
                      <img src={video.cover_url} alt="" />
                    ) : (
                      <div className="poster-fallback">{video.title.slice(0, 1)}</div>
                    )}
                  </div>

                  <div className="watch-banner-copy">
                    <p className="watch-kicker">{typeLabel(video.content_type)}</p>
                    <h1>{video.title}</h1>
                    <div className="watch-chips">
                      {chips.map((chip) => (
                        <span key={chip} className="watch-chip">
                          {chip}
                        </span>
                      ))}
                      {languages.slice(0, 3).map((lang) => (
                        <span key={`lang-${lang}`} className="watch-chip watch-chip-soft">
                          {lang}
                        </span>
                      ))}
                    </div>
                    <p className="watch-synopsis">
                      {video.description || "Sin sinopsis disponible."}
                    </p>
                    <div className="watch-banner-facts">
                      <div>
                        <span>Duración</span>
                        <strong>{durationLabel || "—"}</strong>
                      </div>
                      <div>
                        <span>Año</span>
                        <strong>{video.year || "—"}</strong>
                      </div>
                      <div>
                        <span>Categoría</span>
                        <strong>{video.category || "—"}</strong>
                      </div>
                    </div>
                    <div className="watch-rating-card" aria-label="Puntuación">
                      <div className="watch-rating-score">
                        <span className="watch-rating-score-star" aria-hidden="true">
                          ★
                        </span>
                        <div>
                          <strong>
                            {ratingAvg != null ? Number(ratingAvg).toFixed(1) : "—"}
                          </strong>
                          <small>
                            {ratingCount
                              ? `${ratingCount} voto${ratingCount === 1 ? "" : "s"}`
                              : "Sin votos de usuarios"}
                          </small>
                        </div>
                      </div>
                      <div className="watch-rating" aria-label="Tu puntuación">
                        <span>Tu nota</span>
                        <div className="watch-stars">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button
                              key={n}
                              type="button"
                              className={`watch-star ${myRating >= n ? "on" : ""}`}
                              disabled={ratingBusy}
                              aria-label={`${n} estrellas`}
                              onClick={() => rateVideo(n)}
                            >
                              ★
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section
              className="watch-stage watch-parallax"
              ref={stageParallaxRef}
              data-parallax-speed="0.1"
            >
              <div className="watch-stage-glow watch-parallax-layer" aria-hidden="true" />
              <div className="watch-stage-inner">
                <div
                  data-vjs-player
                  className="player-wrap watch-player"
                  ref={containerRef}
                />
                {playerTrackUi}
              </div>
            </section>

            {actors.length ? (
              <section
                className="watch-cast watch-parallax"
                ref={castParallaxRef}
                data-parallax-speed="0.2"
                aria-label="Reparto"
              >
                <div className="watch-cast-bg watch-parallax-layer" aria-hidden="true" />
                <div className="watch-cast-inner">
                  <header className="watch-cast-head">
                    <p className="watch-kicker">Elenco</p>
                    <h2>Reparto</h2>
                  </header>
                  <div className="watch-cast-grid">
                    {actors.map((actor, index) => (
                      <button
                        key={`${actor.name}-${actor.character || ""}-${actor.tmdb_id || ""}`}
                        type="button"
                        className="cast-card-btn"
                        style={{ "--cast-i": index }}
                        onClick={() => openActor(actor)}
                        title={
                          actor.character
                            ? `${actor.name} — ${actor.character}`
                            : actor.name
                        }
                      >
                        <div className="cast-card-photo">
                          {actor.profile_url ? (
                            <img src={actor.profile_url} alt="" loading="lazy" />
                          ) : (
                            <div className="cast-fallback">{actor.name.slice(0, 1)}</div>
                          )}
                        </div>
                        <strong>{actor.name}</strong>
                        {actor.character ? <span>{actor.character}</span> : null}
                      </button>
                    ))}
                  </div>
                </div>
              </section>
            ) : null}

            {actorModal ? (
              <div
                className="actor-modal-backdrop"
                role="presentation"
                onClick={() => setActorModal(null)}
              >
                <div
                  className="actor-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-label={actorModal.actor.name}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="actor-modal-close"
                    onClick={() => setActorModal(null)}
                    aria-label="Cerrar"
                  >
                    ×
                  </button>
                  <div className="actor-modal-layout">
                    <div className="actor-modal-photo">
                      {actorModal.person?.profile_url_hd ||
                      actorModal.actor.profile_url ? (
                        <img
                          src={
                            actorModal.person?.profile_url_hd ||
                            actorModal.actor.profile_url
                          }
                          alt=""
                        />
                      ) : (
                        <div className="cast-fallback">
                          {actorModal.actor.name.slice(0, 1)}
                        </div>
                      )}
                    </div>
                    <div className="actor-modal-body">
                      <p className="watch-kicker">Reparto</p>
                      <h2>{actorModal.person?.name || actorModal.actor.name}</h2>
                      {actorModal.actor.character ? (
                        <p className="actor-modal-role">
                          Como <strong>{actorModal.actor.character}</strong>
                        </p>
                      ) : null}
                      {actorBusy ? (
                        <p className="muted">Cargando ficha…</p>
                      ) : null}
                      {actorError ? <p className="error">{actorError}</p> : null}
                      {actorModal.person ? (
                        <>
                          <div className="actor-modal-facts">
                            {actorModal.person.known_for_department ? (
                              <span>{actorModal.person.known_for_department}</span>
                            ) : null}
                            {actorModal.person.birthday ? (
                              <span>{actorModal.person.birthday}</span>
                            ) : null}
                            {actorModal.person.place_of_birth ? (
                              <span>{actorModal.person.place_of_birth}</span>
                            ) : null}
                          </div>
                          <p className="actor-modal-bio">
                            {actorModal.person.biography_brief}
                          </p>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        ) : !error ? (
          <p className="muted catalog-status">Preparando reproducción…</p>
        ) : null}
      </div>
    </Layout>
  );
}
